import { useMemo, useRef } from 'react'

/**
 * A simple store implementation that lets you manage and pass down state via
 * a context provider without incurring the performance overhead of rerendering
 * the child and every consumer of the context provider when the state changes.
 * Use this store in combination with `useSyncExternalStore` to allow context
 * consumers to subscribe to a specific slice of the store's state and only
 * rerender when that slice changes.
 */
export function useStore<T> (
  initialState: T
) {
  const state = useRef<T>(initialState)
  const listeners = useRef(new Set<() => void>())

  const store = useMemo(
    () => ({
      setState: (nextState: Partial<T>) => {
        state.current = { ...state.current, ...nextState }
        store.emit()
      },
      subscribe: (listener: () => void) => {
        listeners.current.add(listener)

        return () => {
          listeners.current.delete(listener)
        }
      },
      snapshot: () => {
        return state.current
      },
      emit: () => {
        listeners.current.forEach((listener) => listener())
      },
    }),
    []
  )

  return store
}
